import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import AccordionItem from '../../components/ui/AccordionItem';
import JobDetails from '../../components/support/JobDetails';
import './Admin.css';
import CustomerInfoModal from '../../components/support/CustomerInfoModal';
import ConfirmationModal from '../../components/support/ConfirmationModal';
import TranslatorInfoModal from '../../components/support/TranslatorInfoModal';

const API_URL = process.env.REACT_APP_API_URL;
const AccordionTestPage = () => {
    const { t, i18n } = useTranslation();
    const { token } = useAuth();
    
    const [allJobs, setAllJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [openId, setOpenId] = useState(null);

    // DÜZELTME 1: JobDetails'in ihtiyaç duyduğu tüm state'ler buraya da eklendi.
   const [fees, setFees] = useState({});
    const [jobDetailsCache, setJobDetailsCache] = useState({});
    const [eligibleTranslators, setEligibleTranslators] = useState({});
    const [selectedTranslators, setSelectedTranslators] = useState({});
    const [availableLanguages, setAvailableLanguages] = useState([]);
    const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [jobToCancel, setJobToCancel] = useState(null);
    const [isTranslatorModalOpen, setIsTranslatorModalOpen] = useState(false);
    const [selectedTranslator, setSelectedTranslator] = useState(null);

    const fetchAllJobs = useCallback(async () => {
        if (!token) { setLoading(false); return; }
        try {
            setLoading(true); setError('');
            const [jobsResponse, langsResponse] = await Promise.all([
                fetch(`${API_URL}/api/support/jobs/all`, { headers: { 'Authorization': `Bearer ${token}` } }),
                fetch(`${API_URL}/api/public/languages`)
            ]);
            if (!jobsResponse.ok) throw new Error(t('error_fetch_jobs'));
            if (!langsResponse.ok) throw new Error(t('error_fetch_languages'));
            const jobsData = await jobsResponse.json();
            const langsData = await langsResponse.json();
            setAllJobs(jobsData);
            setAvailableLanguages(langsData.map(lang => ({ value: lang.code, label: t(`lang_${lang.code}`) })));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [token, t]);

    useEffect(() => {
        fetchAllJobs();
    }, [fetchAllJobs]);
    
    const pendingJobs = useMemo(() => allJobs.filter(j => j.status === 'pending_approval'), [allJobs]);

 
  const onJobUpdate = (updatedJob) => {
        const jobId = updatedJob.id;
        setJobDetailsCache(prev => ({ ...prev, [jobId]: { ...prev[jobId], ...updatedJob } }));
        setAllJobs(prev => prev.map(j => j.id === jobId ? { ...j, ...updatedJob } : j));
    };
    const onNewFileAdded = (jobId, newFileData) => {
        setJobDetailsCache(prev => ({ ...prev, [jobId]: { ...prev[jobId], files: [...(prev[jobId]?.files || []), newFileData] } }));
        fetchAllJobs();
    };
    const onFileDeleted = (jobId, deletedFileId, wordCountChange) => {
        setJobDetailsCache(prev => ({ ...prev, [jobId]: { ...prev[jobId], files: prev[jobId]?.files.filter(f => f.id !== deletedFileId) } }));
        fetchAllJobs();
    };
    const onApproveSuccess = () => { setOpenId(null); fetchAllJobs(); };
    const onAssignSuccess = () => { setOpenId(null); fetchAllJobs(); };
    const onCancelJob = (job) => { setJobToCancel(job); setIsCancelModalOpen(true); };
    const handleCancelJob = async () => {
        if (!jobToCancel) return;
        try {
            const response = await fetch(`${API_URL}/api/support/jobs/${jobToCancel.id}/cancel`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
            if (!response.ok) throw new Error(t('error_job_cancel'));
            alert(t('success_job_cancelled'));
            fetchAllJobs();
        } catch (err) { alert(err.message); } finally { setIsCancelModalOpen(false); setJobToCancel(null); }
    };
    const onCustomerNameClick = (customerData) => {
        setSelectedCustomer(customerData);
        setIsCustomerModalOpen(true);
    };
    const onTranslatorNameClick = async (translatorId) => {
        if (!translatorId) return;
        try {
            const response = await fetch(`${API_URL}/api/support/users/${translatorId}/details`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (!response.ok) throw new Error('Çevirmen bilgileri alınamadı.');
            const data = await response.json();
            setSelectedTranslator(data);
            setIsTranslatorModalOpen(true);
        } catch (err) { alert(err.message); }
    };
    const onAssign = async (jobId) => {
        const translatorId = selectedTranslators[jobId];
        const translatorFee = fees[jobId];
        if (!translatorId || !translatorFee) { alert(t('alert_select_translator_and_fee')); return; }
        try {
            const response = await fetch(`${API_URL}/api/support/jobs/${jobId}/assign`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ translatorId, translatorFee }) });
            if (!response.ok) { const data = await response.json(); throw new Error(t(data.messageKey) || t('error_job_assignment')); }
            alert(t('success_job_assigned'));
            onAssignSuccess();
        } catch (err) { alert(err.message); }
    };
    const onLanguageChange = async (jobId, sourceLang, targetLangs) => {
        onJobUpdate({ id: jobId, source_lang: sourceLang, target_langs: targetLangs });
        try {
            await fetch(`${API_URL}/api/support/jobs/${jobId}/languages`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ sourceLang, targetLangs })
            });
        } catch (err) { alert(t('error_language_update_failed')); fetchAllJobs(); }
    };

    const handleToggle = async (id) => {
        const newOpenId = openId === id ? null : id;
        setOpenId(newOpenId);
        if (newOpenId && !jobDetailsCache[newOpenId]) {
            try {
                const [detailsResponse, translatorsResponse] = await Promise.all([
                    fetch(`${API_URL}/api/support/jobs/${id}/details`, { headers: { 'Authorization': `Bearer ${token}` } }),
                    fetch(`${API_URL}/api/support/jobs/${id}/eligible-translators`, { headers: { 'Authorization': `Bearer ${token}` } })
                ]);
                if (!detailsResponse.ok) throw new Error('Detaylar getirilemedi.');
                if (!translatorsResponse.ok) throw new Error(t('error_fetch_translators'));
                const detailedJobData = await detailsResponse.json();
                const translatorsData = await translatorsResponse.json();
                setJobDetailsCache(prev => ({...prev, [id]: detailedJobData}));
                setEligibleTranslators(prev => ({ ...prev, [id]: { loading: false, error: null, data: translatorsData } }));
            } catch (err) { console.error(`İş #${id} için detaylar alınamadı:`, err); }
        }
    };

    if (loading) return <div>{t('loading_jobs')}</div>;
    if (error) return <div>{error}</div>;

    return (
        <>
            <div className="page-container admin-dashboard">
                <h1>{t('pending_approval')}</h1>
                <div className="accordion-container">
                    {pendingJobs.map(job => {
                        const detailedJob = jobDetailsCache[job.id] || job;
                        let targetLangsArray = [];
                        try { if (Array.isArray(detailedJob.target_langs)) { targetLangsArray = detailedJob.target_langs; } else if (typeof detailedJob.target_langs === 'string' && detailedJob.target_langs.startsWith('[')) { targetLangsArray = JSON.parse(detailedJob.target_langs); } } catch (e) {}
                        const title = `#${job.id} - ${job.source_lang.toUpperCase()} → ${targetLangsArray.join(', ').toUpperCase()}`;
                        
                        return (
                            <AccordionItem 
                                key={job.id} id={job.id} title={title}
                                isOpen={openId === job.id} onToggle={handleToggle}
                                content={
                                    openId === job.id && (
                                        <JobDetails 
                                            job={detailedJob} t={t} onJobUpdate={onJobUpdate}
                                            onNewFileAdded={(newFile) => onNewFileAdded(job.id, newFile)}
                                            onFileDeleted={(fileId, wordCount) => onFileDeleted(job.id, fileId, wordCount)}
                                            eligibleTranslators={eligibleTranslators[job.id]}
                                            selectedTranslator={selectedTranslators[job.id]}
                                            onTranslatorSelect={(translatorId) => setSelectedTranslators(p => ({ ...p, [job.id]: translatorId }))}
                                            onAssign={() => onAssign(job.id)}
                                            fees={fees}
                                            onFeeChange={(value) => setFees(p => ({ ...p, [job.id]: value }))}
                                            onLanguageChange={(sourceLang, targetLangs) => onLanguageChange(job.id, sourceLang, targetLangs)}
                                            availableLanguages={availableLanguages}
                                            onCustomerNameClick={() => onCustomerNameClick(detailedJob)}
                                            onTranslatorNameClick={onTranslatorNameClick}
                                            onCancelJob={() => onCancelJob(job)}
                                            onApproveSuccess={onApproveSuccess}
                                            onAssignSuccess={onAssignSuccess}
                                        />
                                    )
                                }
                            />
                        )
                    })}
                </div>
            </div>
            <CustomerInfoModal isOpen={isCustomerModalOpen} onClose={() => setIsCustomerModalOpen(false)} customer={selectedCustomer} />
            <ConfirmationModal isOpen={isCancelModalOpen} onClose={() => setIsCancelModalOpen(false)} onConfirm={handleCancelJob} title={t('confirm_cancel_job_title')} message={t('confirm_cancel_job_message', { jobId: jobToCancel?.id })} />
            <TranslatorInfoModal isOpen={isTranslatorModalOpen} onClose={() => setIsTranslatorModalOpen(false)} translator={selectedTranslator}/>
        </>
    );
};
export default AccordionTestPage;